import React from "react";
import { Button } from "react-bootstrap";
import { AiOutlineExpandAlt } from "react-icons/ai";
import { Link } from "react-router-dom";
import { S3Media } from "../../../third-party-services";

/**
 * Companies Datatable Row Component
 * 
 * Renders individual rows for the companies datatable with support for expandable content,
 * sorting indicators, filter status indicators, and highlighted search text. Manages
 * cell rendering with proper styling based on data type and applied filters/sorts.
 */

// Empty cell placeholder component
const EmptyTableCell = () => <p style={{ textAlign: "center" }}>-</p>;

// Individual table cell component with data rendering, highlighting, and applied indicator attributes
const TableCell = ({
  field,
  value,
  highlight,
  renderContent,
  highlightText,
  getSortAppliedBool,
  getFilterAppliedBool,
}) => (
  <td
    sortapplied={getSortAppliedBool(field.actualName)}
    filterapplied={getFilterAppliedBool(field.actualName)}
    className={field.cName}
  >
    {!value ? (
      <EmptyTableCell />
    ) : renderContent ? (
      renderContent(value)
    ) : (
      <p
        dangerouslySetInnerHTML={{
          __html: highlightText(value, highlight),
        }}
      ></p>
    )}
  </td>
);

// Expandable content container that shows truncated text with expand button for detailed viewing
const ExpandableContent = ({
  value,
  expandData,
  onClick,
  highlight,
  highlightText,
}) => (
  <div className="expandable">
    {typeof value === "string" ? (
      <p
        className="detailcutdata"
        dangerouslySetInnerHTML={{
          __html: highlightText(value, highlight),
        }}
      ></p>
    ) : (
      <div className="detailcutdata">{value}</div>
    )}
    <Button className="expand-button" onClick={() => onClick(expandData)}>
      <AiOutlineExpandAlt />
    </Button>
  </div>
);

// Render table cell component
const RenderTableCell = ({
  hiddenFieldNames,
  getFilterAppliedBool,
  getSortAppliedBool,
  openExpandCard,
  highlightText,
  highlight,
  field,
  actualName,
  value,
  renderContent,
}) => {
  if (
    field.actualName !== actualName ||
    hiddenFieldNames.includes(actualName)
  ) {
    return null;
  }

  return (
    <TableCell
      hiddenFieldNames={hiddenFieldNames}
      getFilterAppliedBool={getFilterAppliedBool}
      getSortAppliedBool={getSortAppliedBool}
      openExpandCard={openExpandCard}
      highlightText={highlightText}
      highlight={highlight}
      field={field}
      value={value}
      renderContent={renderContent}
    />
  );
};

// Table row component
const TableRow = ({ props2 }) => {
  const {
    state,
    argument,
    appliedFilterFieldNames,
    sortConditionsArray,
    tableData,
    fields,
    hiddenFieldNames,
    handleData,
    openExpandCard,
    getCompanyDetails,
    searchApplied,
  } = props2;

  // Highlight text function
  const highlightText = (text, searchTerm) => {
    // Return original input if not a string or if search isn't applied
    if (typeof text !== "string" || !searchApplied) return text;

    // Return original text if search term is empty
    if (!searchTerm || !searchTerm.trim()) return text;

    // Create highlight regex
    const words = searchTerm.trim().split(/\s+/).filter(Boolean);
    const regex = new RegExp(`(${words.join("|")})`, "gi");

    return text.replace(regex, `<span class="highlight">$1</span>`);
  };

  // Check if a filter is applied
  const getFilterAppliedBool = (fieldName) => {
    // console.log("appliedFilterFieldNames", appliedFilterFieldNames);

    // Check if the filter is applied and if any of the applied filter field names start with the given fieldName
    const isFilterApplied =
      argument.filter.applied &&
      appliedFilterFieldNames.some(
        (appliedField) =>
          appliedField.startsWith(`${fieldName}.`) || appliedField === fieldName
      );

    return isFilterApplied ? "true" : "false";
  };

  // Check if a sort is applied
  const getSortAppliedBool = (fieldName) =>
    sortConditionsArray.some((e) => e.actualName === fieldName)
      ? "true"
      : "false";

  // Props for table cells
  const tableCellProps = {
    hiddenFieldNames,
    getFilterAppliedBool,
    getSortAppliedBool,
    openExpandCard,
    highlightText,
    highlight: state.searchTermApplied,
  };

  // Field configuration
  const fieldConfig = {
    tradeName: (company) => ({
      value: company.tradeName,
      renderContent: (value) => (
        <Link
          to={`/company/${company.slug}`}
          target="_blank"
          rel="noopener noreferrer"
          className="shutdowndatalink"
          dangerouslySetInnerHTML={{
            __html: highlightText(value, state.searchTermApplied),
          }}
        />
      ),
    }),
    entityType: (company) => ({
      value: company.entityType,
    }),
    isActive: (company) => ({
      value: company.isActive,
      renderContent: (value) => (
        <p className={`tag ${value ? "green2" : "red"}`}>
          {value ? "Active" : "Inactive"}
        </p>
      ),
    }),
    slug: (company) => ({
      value: company.slug,
      renderContent: (value) => (
        <p className="detailshortdata">
          <Link
            to={`/company/${value}#data-room`}
            target="_blank"
            rel="noopener noreferrer"
            className="shutdowndatalink"
          >
            Pitch deck
          </Link>
        </p>
      ),
    }),
    stageOfDevelopment: (company) => ({
      value: company.stageOfDevelopment,
    }),
    description: (company) => ({
      value: company.description,
      renderContent: (value) => (
        <ExpandableContent
          {...tableCellProps}
          value={value}
          expandData={value}
          onClick={openExpandCard}
        />
      ),
    }),
    reasonsForFailure: (company) => ({
      value: company.reasonsForFailure,
      renderContent: (value) => (
        <ExpandableContent
          {...tableCellProps}
          value={
            <ol className="detailcutdata detailproductlist">
              {value.map((reason, index) =>
                reason ? (
                  <li key={index} className="detailbodydata">
                    {reason.label}
                  </li>
                ) : null
              )}
            </ol>
          }
          expandData={value}
          onClick={openExpandCard}
        />
      ),
    }),
    industry: (company) => ({
      value: company.industry,
    }),
    categories: (company) => ({
      value: company.categories,
      renderContent: (value) => (
        <ExpandableContent
          {...tableCellProps}
          value={value.join(", ")}
          expandData={value.join(", ")}
          onClick={openExpandCard}
        />
      ),
    }),
    location: (company) => ({
      value: company.details.location,
      renderContent: (value) => (
        <p>
          {value.city && (
            <>
              <span
                dangerouslySetInnerHTML={{
                  __html: highlightText(value.city, state.searchTermApplied),
                }}
              ></span>
              <span>, </span>
            </>
          )}
          {value.state && (
            <>
              <span
                dangerouslySetInnerHTML={{
                  __html: highlightText(value.state, state.searchTermApplied),
                }}
              ></span>
              <span>, </span>
            </>
          )}
          {value.country && (
            <span
              dangerouslySetInnerHTML={{
                __html: highlightText(value.country, state.searchTermApplied),
              }}
            ></span>
          )}
        </p>
      ),
    }),
    ownership: (company) => ({
      value: company.ownership,
    }),
    businessModelType: (company) => ({
      value: company.businessModelType,
    }),
    defensibilityMoat: (company) => ({
      value: company.defensibilityMoat,
      renderContent: (value) => (
        <ExpandableContent
          {...tableCellProps}
          value={value.join(", ")}
          expandData={value.join(", ")}
          onClick={openExpandCard}
        />
      ),
    }),
    productReview: (company) => ({
      value: company.productReview,
    }),
    lastFinancingRound: (company) => ({
      value: company.details.lastFinancingRound,
    }),
    totalInvestment: (company) => ({
      value: company.details.totalInvestment,
      renderContent: (value) => (
        <p>
          {value.currency} {value.total}
          {value.symbol}
        </p>
      ),
    }),
    postMoneyValuation: (company) => ({
      value: company.details.postMoneyValuation,
    }),
    lastFinancingDate: (company) => ({
      value: company.details.lastFinancingDate,
    }),
    numOfFundingRounds: (company) => ({
      value: company.details.numOfFundingRounds,
    }),
    numOfInvestors: (company) => ({
      value: company.details.numOfInvestors?.total,
    }),
    investors: (company) => ({
      value: company.investors,
      renderContent: (value) => (
        <ExpandableContent
          {...tableCellProps}
          value={
            <ol className="detailproductlist detailcutdata">
              {value.map((investor, index) =>
                investor ? (
                  <li
                    key={index}
                    className="detailbodydata"
                    dangerouslySetInnerHTML={{
                      __html: highlightText(
                        investor.name,
                        state.searchTermApplied
                      ),
                    }}
                  />
                ) : null
              )}
            </ol>
          }
          expandData={{
            slug: company.slug,
            field: "investors",
            table: "companies",
          }}
          onClick={getCompanyDetails}
        />
      ),
    }),
    revenue: (company) => ({
      value: company.details.revenue,
    }),
    market: (company) => ({
      value: company.details.market,
    }),
    investments: (company) => ({
      value: company.details.investments,
    }),
    numOfAcquisitions: (company) => ({
      value: company.details.numOfAcquisitions,
    }),
    boardSeats: (company) => ({
      value: company.details.boardSeats,
    }),
    competitors: (company) => ({
      value: company.competitors,
      renderContent: (value) => (
        <ExpandableContent
          {...tableCellProps}
          value={
            <ol className="detailproductlist detailcutdata">
              {value.map((competitor, index) =>
                competitor ? (
                  <li key={index} className="detailbodydata">
                    {competitor.name}
                  </li>
                ) : null
              )}
            </ol>
          }
          expandData={{
            slug: company.slug,
            field: "competitors",
            table: "companies",
          }}
          onClick={getCompanyDetails}
        />
      ),
    }),
    similarEntities: (company) => ({
      value: company.similarEntities,
      renderContent: (value) => (
        <ExpandableContent
          {...tableCellProps}
          value={
            <ol className="detailproductlist">
              {value.map((similar, index) =>
                similar ? (
                  <li key={index} className="detailbodydata">
                    {similar.name}
                  </li>
                ) : null
              )}
            </ol>
          }
          expandData={{
            slug: company.slug,
            field: "similarEntities",
            table: "companies",
          }}
          onClick={getCompanyDetails}
        />
      ),
    }),
    management: (company) => ({
      value: company.management,
      renderContent: (value) => (
        <ExpandableContent
          {...tableCellProps}
          value={
            <ol className="detailproductlist detailcutdata">
              {value.map((founder, index) =>
                founder ? (
                  <li
                    key={index}
                    className="detailbodydata"
                    dangerouslySetInnerHTML={{
                      __html: highlightText(
                        founder.name,
                        state.searchTermApplied
                      ),
                    }}
                  />
                ) : null
              )}
            </ol>
          }
          expandData={value}
          onClick={openExpandCard}
        />
      ),
    }),
    numOfEmployees: (company) => ({
      value: company.details.numOfEmployees,
      renderContent: (value) => (
        <p style={{ textAlign: "center" }}>
          {value?.min} - {value?.max}
        </p>
      ),
    }),
    reputation: (company) => ({
      value: company.details.reputation,
    }),
    phoneNumber: (company) => ({
      value: company.details.phoneNumber,
    }),
    operationalDuration: (company) => ({
      value: company.details.year?.lifespan,
      renderContent: (value) => (
        <p>
          {value} {value > 1 ? "years" : "year"}
        </p>
      ),
    }),
    shutdownDate: (company) => ({
      value: company.details.date?.ended,
    }),
    foundedDate: (company) => ({
      value: company.details.date?.started,
    }),
    legalName: (company) => ({
      value: company.legalName,
    }),
    former_legalName: (company) => ({
      value: company.former_legalName,
      renderContent: (value) => (
        <ExpandableContent
          {...tableCellProps}
          value={value.join(", ")}
          expandData={value.join(", ")}
          onClick={openExpandCard}
        />
      ),
    }),
    ticker: (company) => ({
      value: company.ticker,
    }),
    pitchdeck_show: (company) => ({
      value: company.pitchdeck_show,
      renderContent: (value) => (
        <p>
          <Link
            to={`/company/${company.slug}#data-room`}
            target="_blank"
            rel="noopener noreferrer"
            className="shutdowndatalink"
          >
            Pitch deck
          </Link>
        </p>
      ),
    }),
  };

  return (
    <>
      {tableData.results.map((company, index) => (
        <tr key={index}>
          <td>
            <p>
              {(argument.currentPage - 1) * argument.numOfRows + index + 1}.
            </p>
          </td>
          <td>
            <img
              src={`${S3Media}assets/photos/logo/${company.logo}`}
              alt={company.tradeName}
              className="shutteredlogo"
              onClick={() => handleData(company.tradeName)}
            />
          </td>
          {fields.map((field) => {
            const config = fieldConfig[field.actualName];
            if (!config) return null;

            const { value, renderContent } = config(company);
            return (
              <RenderTableCell
                key={field.actualName}
                {...tableCellProps}
                field={field}
                actualName={field.actualName}
                value={value}
                renderContent={renderContent}
              />
            );
          })}
        </tr>
      ))}
    </>
  );
};

export default TableRow;
